import {
  DateInput as NextUIDateInput,
  DateInputProps as NextUIDateInputProps,
} from "@heroui/react";
import {
  CalendarDateInput,
  convertCalendarDateToUnix,
  toHeroUIDateProp,
} from "../../utils/date";
import { getDateInputClassNames } from "../../utils/styles";
import { DateClearButton } from "./date-clear-button";

type Color = Extract<NextUIDateInputProps["color"], "default">;
type LabelPlacement = Extract<
  NextUIDateInputProps["labelPlacement"],
  "outside"
>;
type Variant = Extract<NextUIDateInputProps["variant"], "bordered">;

export type DateInputProps = Omit<
  NextUIDateInputProps,
  "color" | "labelPlacement" | "variant" | "value" | "minValue" | "maxValue"
> & {
  color?: Color;
  isClearable?: boolean;
  labelPlacement?: LabelPlacement;
  maxValue?: number;
  minValue?: number;
  onChange?: (value: number | null) => void;
  ref?: React.Ref<HTMLInputElement>;
  value?: number | null;
  variant?: Variant;
};

export function DateInput({ ref, ...props }: DateInputProps) {
  const {
    isClearable = true,
    maxValue,
    minValue,
    onChange,
    value,
    ...rest
  } = props;

  const handleChange = (calendarDate: CalendarDateInput) => {
    if (!onChange) return;
    onChange(convertCalendarDateToUnix(calendarDate));
  };

  const handleClear = () => {
    onChange?.(null);
  };

  const showClearButton =
    isClearable && value != null && !rest.isDisabled && !rest.isReadOnly;

  return (
    <NextUIDateInput
      {...rest}
      ref={ref}
      classNames={getDateInputClassNames(props.size)}
      color="default"
      data-testid={props.name}
      endContent={
        showClearButton ? (
          <DateClearButton
            id={props.name ? `${props.name}-clear` : "date-input-clear"}
            onClear={handleClear}
          />
        ) : undefined
      }
      labelPlacement="outside"
      maxValue={toHeroUIDateProp<NextUIDateInputProps["maxValue"]>(maxValue)}
      minValue={toHeroUIDateProp<NextUIDateInputProps["minValue"]>(minValue)}
      value={toHeroUIDateProp<NextUIDateInputProps["value"]>(value)}
      variant="bordered"
      onChange={handleChange}
    />
  );
}

DateInput.displayName = "RecareUI.DateInput";
